import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { requestPasswordReset } from '@/lib/api';
import { motion } from 'framer-motion';
import { KeyRound, MailCheck, AlertCircle, ArrowLeft } from 'lucide-react';

export function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [sent, setSent] = useState(false);

    const extractDetail = (err: any, fallback: string) => {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map((e: any) => e.msg).join(', ');
        if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
        return fallback;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!email.trim() || isSubmitting) return;
        setIsSubmitting(true);
        setError('');
        try {
            await requestPasswordReset(email.trim());
            setSent(true);
        } catch (err: any) {
            // Unregistered email -> backend returns 404 with the explicit message.
            setError(extractDetail(err, 'リクエストに失敗しました。時間をおいて再度お試しください。'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0c0a09] font-serif relative overflow-hidden p-4">
            <div className="absolute inset-0 z-0 opacity-40 bg-[url('https://images.unsplash.com/photo-1506459225024-1428097a7e18?q=80&w=2574&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay"></div>
            <div className="absolute inset-0 z-0 bg-gradient-to-t from-black via-black/80 to-transparent"></div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                className="w-full max-w-md p-1 relative z-10"
            >
                <div className="bg-[#1c1917] border border-[#d4af37] shadow-[0_0_50px_rgba(212,175,55,0.1)] rounded-sm p-8 text-[#d4af37]">
                    <div className="text-center mb-8">
                        <KeyRound className="mx-auto text-[#d4af37] mb-4" size={36} />
                        <h1 className="text-2xl font-bold tracking-widest text-[#fcd34d]">ルームキーの再発行</h1>
                        <p className="text-sm text-[#d4af37]/70 mt-3 tracking-wider leading-relaxed">
                            ご登録のメールアドレスに、パスワード再設定用のリンクをお送りします。
                        </p>
                        <div className="h-px w-24 bg-[#d4af37] mx-auto mt-6 opacity-50"></div>
                    </div>

                    {sent ? (
                        <div className="text-center py-6">
                            <div className="flex justify-center mb-5">
                                <div className="w-16 h-16 bg-[#d4af37]/20 rounded-full flex items-center justify-center border border-[#d4af37]">
                                    <MailCheck className="w-8 h-8 text-[#fcd34d]" />
                                </div>
                            </div>
                            <p className="text-[#d4af37]/90 leading-relaxed tracking-wider">
                                再設定用リンクをメールにお送りしました。<br />
                                メールをご確認のうえ、リンクからお手続きください。<br />
                                <span className="text-xs text-[#d4af37]/60">（有効期限: 1時間）</span>
                            </p>
                            <Button
                                type="button"
                                onClick={() => navigate('/login')}
                                className="w-full mt-8 bg-[#d4af37] hover:bg-[#ffe58f] text-black font-serif font-bold h-12 uppercase tracking-widest"
                            >
                                ログイン画面へ戻る
                            </Button>
                            <button
                                type="button"
                                onClick={() => navigate('/login?contact=password')}
                                className="w-full mt-4 text-xs text-[#d4af37]/70 hover:text-[#fcd34d] underline"
                            >
                                メールが届かない場合は、管理人に問い合わせる
                            </button>
                        </div>
                    ) : (
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div>
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">メールアドレス</label>
                                <Input
                                    type="email"
                                    className="bg-[#0c0a09] border-[#d4af37]/30 text-[#fcd34d] placeholder:text-[#d4af37]/20 h-12 pl-4 focus:ring-[#d4af37] focus:border-[#d4af37]"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            {error && (
                                <div className="text-red-400 text-sm text-center border border-red-900/50 bg-red-950/20 p-3 flex items-center gap-2 justify-center">
                                    <AlertCircle size={18} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-[#d4af37] hover:bg-[#ffe58f] text-black font-serif font-bold h-12 uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-60"
                            >
                                {isSubmitting ? <span className="animate-pulse">送信中...</span> : <span>再設定リンクを送る</span>}
                            </motion.button>

                            <div className="border border-[#d4af37]/20 bg-black/20 p-3 text-xs text-[#d4af37]/70 leading-relaxed text-center">
                                登録したメールアドレスが使えない、または分からない場合は
                                <button
                                    type="button"
                                    onClick={() => navigate('/login?contact=password')}
                                    className="block w-full mt-2 underline text-[#fcd34d] hover:text-[#ffe58f]"
                                    data-testid="forgot-password-contact"
                                >
                                    管理人に問い合わせる
                                </button>
                            </div>

                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                className="w-full text-xs text-[#d4af37]/60 hover:text-[#fcd34d] flex items-center justify-center gap-1"
                            >
                                <ArrowLeft size={12} />
                                ログイン画面へ戻る
                            </button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
